import React, { useState, useEffect, useCallback, useContext } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { Spinner, Button } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';

registerLocale('es', es);

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

const formatDuration = minutes => {
  if (!minutes || minutes <= 0) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const formatLocalDateStr = dt => {
  if (!dt) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const TimeMetricCard = ({
  label,
  icon,
  iconColor = '#6366f1',
  lineColor = '#6366f1',
  accentClass = 'accent-purple',
  valueHoy = 0,
  value3Dias = 0,
  value7Dias = 0,
  metricKey,
  countKey,
  agentId = '',
  isLoading = false
}) => {
  const { token } = useToken();
  const { config: { isDark } = {} } = useContext(AppContext);

  const [selectedDateObj, setSelectedDateObj] = useState(new Date());
  const [chartData, setChartData] = useState([]);
  const [isLoadingChart, setIsLoadingChart] = useState(false);

  // Estados para mostrar/ocultar cada métrica en la gráfica
  const [visibleSeries, setVisibleSeries] = useState({
    today: true,
    threeDays: true,
    sevenDays: true
  });

  const toggleSeries = key => {
    setVisibleSeries(prev => {
      const next = { ...prev, [key]: !prev[key] };
      // Mantener al menos una métrica visible para que la gráfica no quede vacía
      if (!next.today && !next.threeDays && !next.sevenDays) {
        return prev;
      }
      return next;
    });
  };

  const isAll = visibleSeries.today && visibleSeries.threeDays && visibleSeries.sevenDays;

  const handleToggleAll = () => {
    if (isAll) {
      // Si todas están activas, regresar a ver solo Hoy
      setVisibleSeries({ today: true, threeDays: false, sevenDays: false });
    } else {
      // Activar todas
      setVisibleSeries({ today: true, threeDays: true, sevenDays: true });
    }
  };

  const selectedDateStr = formatLocalDateStr(selectedDateObj);
  const todayStr = formatLocalDateStr(new Date());
  const isToday = selectedDateStr === todayStr;

  const handlePrevDay = () => {
    const prev = new Date(selectedDateObj);
    prev.setDate(prev.getDate() - 1);
    setSelectedDateObj(prev);
  };

  const handleNextDay = () => {
    const next = new Date(selectedDateObj);
    next.setDate(next.getDate() + 1);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (next <= today) {
      setSelectedDateObj(next);
    }
  };

  const fetchHourlyChartData = useCallback(async () => {
    if (!token) return;
    setIsLoadingChart(true);
    try {
      const params = new URLSearchParams();
      const hasAgent = agentId && agentId !== 'todos' && agentId !== 'all';
      if (hasAgent) {
        params.set('idUsuarioAsignado', agentId);
      }
      if (selectedDateStr) {
        params.set('dFecha', selectedDateStr);
      }

      const resp = await fetch(
        `/api/SupportEngine/GetDashboardHourlyTimeMetricsChart?${params.toString()}`,
        {
          headers: {
            Accept: 'application/json',
            Authorization: 'Bearer ' + token
          }
        }
      );

      if (resp.ok) {
        const json = await resp.json();
        const data = json.data || json || [];
        setChartData(Array.isArray(data) ? data : []);
      }
    } catch {
      // Silencioso
    } finally {
      setIsLoadingChart(false);
    }
  }, [token, agentId, selectedDateStr]);

  useEffect(() => {
    fetchHourlyChartData();
  }, [fetchHourlyChartData]);

  // Colores adaptados a Dark / Light Mode
  const textColor = isDark ? '#94a3b8' : '#64748b';
  const axisLineColor = isDark ? 'rgba(255, 255, 255, 0.15)' : '#e2e8f0';
  const splitLineColor = isDark ? 'rgba(255, 255, 255, 0.07)' : '#f1f5f9';

  const categories = chartData.map(d => d.vHora || `${String(d.iHora).padStart(2, '0')}:00`);
  const seriesDataHours = chartData.map(d => {
    const mins = d[metricKey] || 0;
    return Number((mins / 60).toFixed(2));
  });

  const selectedDayAvg = React.useMemo(() => {
    if (isToday) return valueHoy;
    const totalMinutes = chartData.reduce((acc, curr) => acc + (curr[metricKey] || 0), 0);
    const nonZeroHours = chartData.filter(d => (d[metricKey] || 0) > 0).length;
    return nonZeroHours > 0 ? Math.round(totalMinutes / nonZeroHours) : 0;
  }, [isToday, valueHoy, chartData, metricKey]);

  const todayLabel = isToday ? 'Hoy' : 'Día sel.';
  const val3DiasHours = Number((value3Dias / 60).toFixed(2));
  const val7DiasHours = Number((value7Dias / 60).toFixed(2));

  // Construcción dinámica de las series activas
  const series = [];

  if (visibleSeries.today) {
    series.push({
      name: todayLabel,
      type: 'line',
      data: seriesDataHours,
      smooth: true,
      symbol: 'circle',
      symbolSize: 4,
      showSymbol: false,
      itemStyle: { color: lineColor },
      lineStyle: { width: 2.5, color: lineColor },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color: `${lineColor}35` },
          { offset: 1, color: `${lineColor}03` }
        ])
      }
    });
  }

  if (visibleSeries.threeDays) {
    series.push({
      name: '3 días Prom',
      type: 'line',
      data: categories.map(() => val3DiasHours),
      showSymbol: false,
      itemStyle: { color: '#10b981' },
      lineStyle: { width: 2, type: 'dashed', color: '#10b981' }
    });
  }

  if (visibleSeries.sevenDays) {
    series.push({
      name: '7 días Prom',
      type: 'line',
      data: categories.map(() => val7DiasHours),
      showSymbol: false,
      itemStyle: { color: '#f59e0b' },
      lineStyle: { width: 2, type: 'dashed', color: '#f59e0b' }
    });
  }

  const chartOptions = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: isDark ? '#1e293b' : '#ffffff',
      borderColor: isDark ? '#334155' : '#e2e8f0',
      borderWidth: 1,
      textStyle: { color: isDark ? '#f1f5f9' : '#0f172a' },
      padding: [8, 12],
      extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); border-radius: 8px;',
      formatter: params => {
        if (!params || !params.length) return '';
        const idx = params[0].dataIndex;
        const rawItem = chartData[idx] || {};
        const count = rawItem[countKey] || 0;
        const mins = rawItem[metricKey] || 0;

        let html = `<div style="font-size: 0.78rem; font-weight: 600; margin-bottom: 4px;">Hora del día: ${params[0].name}</div>`;
        if (visibleSeries.today) {
          html += `<div style="font-size: 0.74rem; color: ${lineColor}; margin-bottom: 2px;">● ${todayLabel} (Tiempo prom.): <b>${formatDuration(mins)}</b> (${count} tickets)</div>`;
        }
        if (visibleSeries.threeDays) {
          html += `<div style="font-size: 0.74rem; color: #10b981; margin-bottom: 2px;">--- 3 días Prom: <b>${formatDuration(value3Dias)}</b></div>`;
        }
        if (visibleSeries.sevenDays) {
          html += `<div style="font-size: 0.74rem; color: #f59e0b;">--- 7 días Prom: <b>${formatDuration(value7Dias)}</b></div>`;
        }
        return html;
      }
    },
    grid: {
      top: 15,
      right: 12,
      bottom: 25,
      left: 10,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: categories,
      boundaryGap: false,
      axisLine: { lineStyle: { color: axisLineColor } },
      axisTick: { alignWithLabel: true, lineStyle: { color: axisLineColor } },
      axisLabel: {
        color: textColor,
        fontSize: 10,
        interval: 3, // Muestra 00:00, 04:00, 08:00, 12:00, 16:00, 20:00
        formatter: val => val
      }
    },
    yAxis: {
      type: 'value',
      min: 0,
      minInterval: 1,
      axisLine: { show: true, lineStyle: { color: axisLineColor } },
      axisLabel: {
        color: textColor,
        fontSize: 10,
        formatter: val => `${val}`
      },
      splitLine: { lineStyle: { color: splitLineColor, type: 'dashed' } }
    },
    series
  };

  return (
    <div className={`se-sla-card ${accentClass} h-100 p-3`}>
      {/* Header: Label a la izquierda y Selector de fecha a la derecha */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={icon} style={{ color: iconColor, fontSize: '1rem' }} />
          <span className="se-metric-label mb-0" style={{ fontSize: '0.80rem' }}>
            {label}
          </span>
        </div>

        {/* Selector de fecha compacto con flechas */}
        <div
          className="d-flex align-items-center bg-light rounded-2 px-1 border"
          style={{ height: '28px', backgroundColor: isDark ? '#1e293b' : '#f8fafc' }}
        >
          <Button
            variant="link"
            size="sm"
            className="p-0 text-secondary"
            onClick={handlePrevDay}
            title="Día anterior"
            style={{ width: '20px', height: '20px' }}
          >
            <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '0.65rem' }} />
          </Button>

          <div className="d-flex align-items-center px-1">
            <FontAwesomeIcon
              icon={faCalendarAlt}
              className="text-primary me-1"
              style={{ fontSize: '0.72rem' }}
            />
            <DatePicker
              selected={selectedDateObj}
              onChange={date => setSelectedDateObj(date || new Date())}
              maxDate={new Date()}
              locale="es"
              dateFormat="d MMM yyyy"
              className="form-control form-control-sm border-0 bg-transparent py-0 px-0 text-center"
              style={{
                width: '85px',
                fontSize: '0.75rem',
                cursor: 'pointer',
                fontWeight: 600,
                color: isDark ? '#e2e8f0' : '#1e293b'
              }}
            />
          </div>

          <Button
            variant="link"
            size="sm"
            className="p-0 text-secondary"
            onClick={handleNextDay}
            disabled={isToday}
            title="Día siguiente"
            style={{ width: '20px', height: '20px', opacity: isToday ? 0.3 : 1 }}
          >
            <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.65rem' }} />
          </Button>
        </div>
      </div>

      {/* Contenido: Bloque métricas a la izquierda + Gráfica a la derecha */}
      <div className="row g-2 align-items-center mt-1">
        {/* Columna Izquierda: Número grande HOY + Promedios 3 y 7 días interactivos */}
        <div className="col-12 col-sm-5 col-md-5 col-lg-4">
          <div className="d-flex flex-column justify-content-center">
            {/* Número grande: HOY (o día seleccionado) con toggle */}
            <div
              role="button"
              onClick={() => toggleSeries('today')}
              title="Clic para mostrar / ocultar en la gráfica"
              style={{
                cursor: 'pointer',
                opacity: visibleSeries.today ? 1 : 0.4,
                transition: 'opacity 0.2s'
              }}
            >
              <div className="se-metric-value mb-1" style={{ fontSize: '1.65rem', lineHeight: '1.2' }}>
                {isLoading || isLoadingChart ? (
                  <Spinner animation="border" size="sm" variant="secondary" />
                ) : (
                  formatDuration(selectedDayAvg)
                )}
              </div>

              {/* Subtítulo indicando que el número grande es Hoy o del día con indicador */}
              <div
                className="d-flex align-items-center gap-1 mb-2 fw-semibold"
                style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b' }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: lineColor,
                    display: 'inline-block'
                  }}
                />
                <span>{isToday ? 'Promedio de hoy' : 'Promedio del día'}</span>
                <span style={{ fontSize: '0.65rem', opacity: 0.75 }}>
                  {visibleSeries.today ? '(visible)' : '(oculto)'}
                </span>
              </div>
            </div>

            {/* Números chicos grisáceos interactivos: 3 días Prom y 7 días Prom */}
            <div className="d-flex flex-column gap-1 pt-1 border-top" style={{ borderColor: isDark ? '#334155' : '#e2e8f0' }}>
              <div
                role="button"
                onClick={() => toggleSeries('threeDays')}
                title="Clic para mostrar / ocultar en la gráfica"
                className="d-flex justify-content-between align-items-center py-1 px-2 rounded-2"
                style={{
                  fontSize: '0.75rem',
                  color: isDark ? '#94a3b8' : '#64748b',
                  cursor: 'pointer',
                  opacity: visibleSeries.threeDays ? 1 : 0.4,
                  backgroundColor: visibleSeries.threeDays
                    ? (isDark ? 'rgba(16, 185, 129, 0.12)' : '#ecfdf5')
                    : 'transparent',
                  border: visibleSeries.threeDays ? '1px solid #10b98140' : '1px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                <div className="d-flex align-items-center gap-1">
                  <span style={{ width: 10, height: 2, borderTop: '2px dashed #10b981', display: 'inline-block' }} />
                  <span>3 días Prom</span>
                </div>
                <span className="fw-semibold ms-1" style={{ color: visibleSeries.threeDays ? '#10b981' : (isDark ? '#cbd5e1' : '#475569') }}>
                  {formatDuration(value3Dias)}
                </span>
              </div>

              <div
                role="button"
                onClick={() => toggleSeries('sevenDays')}
                title="Clic para mostrar / ocultar en la gráfica"
                className="d-flex justify-content-between align-items-center py-1 px-2 rounded-2"
                style={{
                  fontSize: '0.75rem',
                  color: isDark ? '#94a3b8' : '#64748b',
                  cursor: 'pointer',
                  opacity: visibleSeries.sevenDays ? 1 : 0.4,
                  backgroundColor: visibleSeries.sevenDays
                    ? (isDark ? 'rgba(245, 158, 11, 0.12)' : '#fffbeb')
                    : 'transparent',
                  border: visibleSeries.sevenDays ? '1px solid #f59e0b40' : '1px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                <div className="d-flex align-items-center gap-1">
                  <span style={{ width: 10, height: 2, borderTop: '2px dashed #f59e0b', display: 'inline-block' }} />
                  <span>7 días Prom</span>
                </div>
                <span className="fw-semibold ms-1" style={{ color: visibleSeries.sevenDays ? '#f59e0b' : (isDark ? '#cbd5e1' : '#475569') }}>
                  {formatDuration(value7Dias)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Columna Derecha: Gráfica con Eje X (Hora) y Eje Y (Value/Minutos) */}
        <div className="col-12 col-sm-7 col-md-7 col-lg-8 position-relative">
          {/* Pills compactos para alternar series directamente arriba de la gráfica */}
          <div className="d-flex align-items-center justify-content-end gap-1 mb-1 flex-wrap">
            <button
              type="button"
              onClick={handleToggleAll}
              className="btn btn-sm py-0 px-2 rounded-pill"
              style={{
                fontSize: '0.68rem',
                border: isDark ? '1px solid #475569' : '1px solid #cbd5e1',
                backgroundColor: isAll
                  ? (isDark ? '#334155' : '#e2e8f0')
                  : 'transparent',
                color: isDark ? '#e2e8f0' : '#475569',
                fontWeight: isAll ? 600 : 400,
                transition: 'all 0.2s'
              }}
              title={isAll ? 'Mostrar solo Hoy' : 'Mostrar todas las métricas'}
            >
              Todas
            </button>
            <button
              type="button"
              onClick={() => toggleSeries('today')}
              className="btn btn-sm py-0 px-2 rounded-pill"
              style={{
                fontSize: '0.68rem',
                border: `1px solid ${lineColor}`,
                backgroundColor: visibleSeries.today ? lineColor : 'transparent',
                color: visibleSeries.today ? '#ffffff' : lineColor,
                transition: 'all 0.2s'
              }}
            >
              ● {todayLabel}
            </button>
            <button
              type="button"
              onClick={() => toggleSeries('threeDays')}
              className="btn btn-sm py-0 px-2 rounded-pill"
              style={{
                fontSize: '0.68rem',
                border: '1px solid #10b981',
                backgroundColor: visibleSeries.threeDays ? '#10b981' : 'transparent',
                color: visibleSeries.threeDays ? '#ffffff' : '#10b981',
                transition: 'all 0.2s'
              }}
            >
              --- 3 días
            </button>
            <button
              type="button"
              onClick={() => toggleSeries('sevenDays')}
              className="btn btn-sm py-0 px-2 rounded-pill"
              style={{
                fontSize: '0.68rem',
                border: '1px solid #f59e0b',
                backgroundColor: visibleSeries.sevenDays ? '#f59e0b' : 'transparent',
                color: visibleSeries.sevenDays ? '#ffffff' : '#f59e0b',
                transition: 'all 0.2s'
              }}
            >
              --- 7 días
            </button>
          </div>
          {isLoadingChart ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: '140px' }}
            >
              <Spinner animation="border" size="sm" variant="secondary" />
            </div>
          ) : (
            <div style={{ height: '140px', width: '100%' }}>
              <ReactEChartsCore
                echarts={echarts}
                option={chartOptions}
                notMerge={true}
                lazyUpdate={true}
                style={{ height: '100%', width: '100%' }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeMetricCard;
